import { useEffect } from "react";
import { NavigateFunction } from "react-router-dom";

import { AssignStatusFilter, PriorityFilterStatus, SignalFilterStatus } from "@/enum/list.enum";

import { IFilterWithValues } from "../../../List/components/FilterModal";
import { CustomListsAutocompleteForSequencerItem } from "../../../List/components/CustomListsAutocompleteForSequencer";

interface UseReadyToSendUrlSyncArgs {
	selectedAssignStatus: AssignStatusFilter[];
	priorityFilterStatus: PriorityFilterStatus[];
	signalFilterStatus: SignalFilterStatus[];
	selectedLists: CustomListsAutocompleteForSequencerItem[];
	filter: IFilterWithValues | undefined;
	query: string;
	setSearchValue: (value: string) => void;
	navigate: NavigateFunction;
}

/**
 * Keeps Review-to-send's assign-status / priority / signal / list-scope / filter / search
 * selections mirrored into the URL query string, and seeds the search box from it on mount.
 */
export function useReadyToSendUrlSync({
	selectedAssignStatus,
	priorityFilterStatus,
	signalFilterStatus,
	selectedLists,
	filter,
	query,
	setSearchValue,
	navigate,
}: UseReadyToSendUrlSyncArgs) {
	useEffect(() => {
		const params = new URLSearchParams(location.search);

		setSearchValue(params.get("searchValue") || "");
	}, []);

	useEffect(() => {
		const params = new URLSearchParams(location.search);

		params.set("assignStatus", JSON.stringify(selectedAssignStatus));
		navigate({ search: params.toString() }, { replace: true });
	}, [selectedAssignStatus, navigate]);

	useEffect(() => {
		const params = new URLSearchParams(location.search);

		params.set("priorityFilterStatus", JSON.stringify(priorityFilterStatus));
		navigate({ search: params.toString() }, { replace: true });
	}, [priorityFilterStatus, location.search, navigate]);

	useEffect(() => {
		const params = new URLSearchParams(location.search);

		if (
			signalFilterStatus &&
			signalFilterStatus.length > 0 &&
			!signalFilterStatus.includes(SignalFilterStatus.ALL)
		) {
			params.set("signalFilterStatus", JSON.stringify(signalFilterStatus));
		} else {
			params.delete("signalFilterStatus");
		}

		navigate({ search: params.toString() }, { replace: true });
	}, [signalFilterStatus, location.search, navigate]);

	useEffect(() => {
		const params = new URLSearchParams(location.search);

		if (selectedLists.length > 0) {
			params.set("listIds", JSON.stringify(selectedLists));
		} else {
			params.delete("listIds");
		}

		navigate({ search: params.toString() }, { replace: true });
	}, [selectedLists, navigate]);

	useEffect(() => {
		// Update the URL when the filter changes
		const params = new URLSearchParams(location.search);

		if (filter) {
			params.set("filter", JSON.stringify(filter)); // Set filter parameter
		} else {
			params.delete("filter"); // Remove filter if empty
		}

		navigate({ search: params.toString() }, { replace: true });
	}, [filter, location.search, navigate]);

	useEffect(() => {
		const params = new URLSearchParams(location.search);

		if (query) {
			params.set("searchValue", query); // Set searchValue parameter
		} else {
			params.delete("searchValue"); // Remove searchValue if empty
		}

		navigate({ search: params.toString() }, { replace: true });
	}, [query, navigate]);
}
